import { motion, useReducedMotion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import SEO from '@/components/SEO';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Lightbulb, 
  Shield, 
  RefreshCw, 
  Heart,
  BadgeCheck,
  Handshake,
  Eye,
  Target,
  Users2,
  Presentation as PresentationIcon
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import companyHero from '@/assets/company-hero.jpg';
import whoWeAreImage from '@/assets/who-we-are.jpg';
import historyImg1 from '@/assets/history/history-1.jpg';
import historyImg2 from '@/assets/history/history-2.jpg';
import historyImg3 from '@/assets/history/history-3.jpg';
import historyImg4 from '@/assets/history/history-4.jpg';
import historyImg5 from '@/assets/history/history-5.jpg';

const historyCollageImages = [historyImg1, historyImg2, historyImg3, historyImg4, historyImg5];

const StatCircle = ({ value, suffix = '', label, delay = 0 }: { value: number; suffix?: string; label: string; delay?: number }) => {
  const shouldReduceMotion = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const [display, setDisplay] = useState(0);
  const [progress, setProgress] = useState(0);
  const radius = 70;
  const circumference = 2 * Math.PI * radius;

  useEffect(() => {
    if (!inView) return;
    if (shouldReduceMotion) {
      setDisplay(value);
      setProgress(1);
      return;
    }
    const duration = 2;
    const startTime = performance.now() + delay * 1000;
    let raf: number;
    const tick = (now: number) => {
      const t = Math.min(Math.max((now - startTime) / (duration * 1000), 0), 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(value * eased));
      setProgress(eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, delay, shouldReduceMotion]);

  return (
    <div ref={ref} className="flex flex-col items-center text-center">
      <div className="relative w-44 h-44 md:w-52 md:h-52">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 160 160">
          <circle cx="80" cy="80" r={radius} stroke="hsl(var(--muted))" strokeWidth="8" fill="none" />
          <circle
            cx="80"
            cy="80"
            r={radius}
            stroke="#0077B6"
            strokeWidth="8"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - progress)}
            style={{ transition: 'stroke-dashoffset 0.05s linear' }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-4xl md:text-5xl font-bold text-[#003F72]">
            {display}{suffix}
          </span>
        </div>
      </div>
      <p className="mt-4 text-base md:text-lg text-[#003F72]/80 font-medium max-w-[14rem]">{label}</p>
    </div>
  );
};

const Company = () => {
  const { t } = useLanguage();
  const shouldReduceMotion = useReducedMotion();
  const navigate = useNavigate();

  const values = [
    {
      icon: Shield,
      titleEn: "Integrity",
      titleEl: "ΑΚΕΡΑΙΟΤΗΤΑ",
      descEn: "We maintain honesty, transparency and ethics in all our transactions, ensuring trust and respect.",
      descEl: "Διατηρούμε ειλικρίνεια, διαφάνεια και ηθική σε όλες τις συναλλαγές μας διασφαλίζοντας εμπιστοσύνη και σεβασμό.",
      color: "text-[#003F72]",
      bgColor: "bg-[#003F72]/10"
    },
    {
      icon: Heart,
      titleEn: "Customer-centricity",
      titleEl: "ΠΕΛΑΤΟΚΕΝΤΡΙΚΟΤΗΤΑ",
      descEn: "We put our customers' needs and satisfaction first, for the creation of long-term partnerships.",
      descEl: "Βάζουμε τις ανάγκες και την ικανοποίηση των πελατών μας πρώτες, για τη δημιουργία μακροχρόνιων συνεργασιών.",
      color: "text-[#003F72]",
      bgColor: "bg-[#003F72]/10"
    },
    {
      icon: BadgeCheck,
      titleEn: "Reliability",
      titleEl: "ΑΞΙΟΠΙΣΤΙΑ",
      descEn: "We provide stable quality and reliable results, on which our customers and partners can rely.",
      descEl: "Παρέχουμε σταθερή ποιότητα και αξιόπιστα αποτελέσματα, στα οποία μπορούν να βασιστούν οι πελάτες και οι συνεργάτες μας.",
      color: "text-[#003F72]",
      bgColor: "bg-[#003F72]/10"
    },
    {
      icon: Handshake,
      titleEn: "Collaboration",
      titleEl: "ΣΥΝΕΡΓΑΣΙΑ",
      descEn: "We promote a culture of teamwork for achieving common goals within the company and with our partners.",
      descEl: "Προωθούμε την κουλτούρα της ομαδικής εργασίας για την επίτευξη κοινών στόχων εντός εταιρίας και με τους συνεργάτες μας.",
      color: "text-[#003F72]",
      bgColor: "bg-[#003F72]/10"
    },
    {
      icon: Lightbulb,
      titleEn: "Innovation",
      titleEl: "ΚΑΙΝΟΤΟΜΙΑ",
      descEn: "We constantly seek new ideas, technologies and methods, at the forefront of developments, to provide pioneering solutions.",
      descEl: "Αναζητούμε συνεχώς νέες ιδέες, τεχνολογίες και μεθόδους, στην αιχμή των εξελίξεων, για να παρέχουμε πρωτοποριακές λύσεις.",
      color: "text-[#003F72]",
      bgColor: "bg-[#003F72]/10"
    },
    {
      icon: RefreshCw,
      titleEn: "Flexibility",
      titleEl: "ΕΥΕΛΙΞΙΑ",
      descEn: "We adapt quickly to changes in technology and markets to stay ahead of the competition.",
      descEl: "Προσαρμοζόμαστε γρήγορα στις αλλαγές της τεχνολογίας και των αγορών για να παραμένουμε μπροστά από τον ανταγωνισμό.",
      color: "text-[#003F72]",
      bgColor: "bg-[#003F72]/10"
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title={t('Εταιρεία', 'Company')}
        description={t('Η TFT - Tesco Food Technology. Πρωτοπόρος στις λύσεις επεξεργασίας και συσκευασίας τροφίμων από το 1963.', 'TFT - Tesco Food Technology. Pioneer in food processing and packaging solutions since 1963.')}
        canonical="https://www.tft.gr/company"
      />
      <Navigation />

      <main className="flex-1">
        {/* Hero / Intro Section */}
        <section className="relative py-24 md:py-32 overflow-hidden">
          {/* Background Collage */}
          <div className="absolute inset-0 grid grid-cols-2 md:grid-cols-5 grid-rows-2 md:grid-rows-1 gap-0">
            {historyCollageImages.map((src, i) => (
              <motion.div
                key={i}
                initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, scale: 1.05 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.2, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                className={`relative overflow-hidden ${i === 4 ? 'hidden md:block' : ''}`}
              >
                <img
                  src={src}
                  alt={t('Ιστορικό έργο TFT', 'TFT historical project')}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </motion.div>
            ))}
          </div>
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#003F72]/90 via-[#003F72]/85 to-[#003F72]/85" />
          
          <div className="container mx-auto px-4 text-center max-w-5xl relative z-10">
            <motion.h1 
              initial={shouldReduceMotion ? {} : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight tracking-tight"
              style={{ lineHeight: 1.2 }}
            >
              Tesco Food Technology
            </motion.h1>
            <motion.p
              initial={shouldReduceMotion ? {} : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.18 }}
              className="mt-4 text-lg md:text-2xl lg:text-3xl text-white font-semibold max-w-4xl mx-auto"
              style={{ lineHeight: 1.35 }}
            >
              {t(
                'Πρωταγωνιστές στις εφαρμογές Τεχνολογίας για τη Βιομηχανία Τροφίμων και Ποτών, από το 1963',
                'Leaders in Technology applications for the Food and Beverage Industry, since 1963'
              )}
            </motion.p>
          </div>
        </section>

        {/* Who We Are Section */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4 max-w-7xl">
            <motion.h2 
              initial={shouldReduceMotion ? {} : { opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6 }}
              className="text-3xl md:text-5xl font-bold text-[#003F72] mb-12 text-center"
              style={{ lineHeight: 1.2 }}
            >
              {t('Ποιοί Είμαστε', 'Who We Are')}
            </motion.h2>
            
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              {/* Text Content - staggered children */}
              <motion.div 
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={{
                  hidden: {},
                  visible: { transition: { staggerChildren: 0.18, delayChildren: 0.1 } }
                }}
                className="space-y-6"
              >
                <motion.p
                  variants={{
                    hidden: shouldReduceMotion ? {} : { opacity: 0, x: -30 },
                    visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
                  }}
                  className="text-2xl md:text-3xl text-[#003F72] font-medium"
                >
                  {t(
                    'Τεχνολογία. Συνεργασία. Αποτέλεσμα.',
                    'Technology. Collaboration. Results.'
                  )}
                </motion.p>
                <motion.p
                  variants={{
                    hidden: shouldReduceMotion ? {} : { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } }
                  }}
                  className="text-[#003F72]/70 text-lg md:text-xl leading-relaxed"
                >
                  {t(
                    'Είμαστε η μόνη εταιρία στην Ελλάδα που προσφέρουμε ολοκληρωμένες λύσεις στις Βιομηχανίες Τροφίμων και Ποτών. Από μελέτες για την εκπόνηση ενός έργου, τον εξοπλισμό με μηχανήματα, ανταλλακτικά και αναλώσιμα επεξεργασίας, ψύξης, συσκευασίας, εργαστηριακού εξοπλισμού, μέχρι και τη συνολική δημιουργία ή συντήρηση μιας παραγωγικής μονάδας.',
                    'We are the only company in Greece offering integrated solutions for the Food and Beverage Industries. From project design studies, equipment with machinery, spare parts and consumables for processing, refrigeration, packaging and laboratory equipment, to the complete creation or maintenance of a production facility.'
                  )}
                </motion.p>
                <motion.p
                  variants={{
                    hidden: shouldReduceMotion ? {} : { opacity: 0, scale: 0.95 },
                    visible: { opacity: 1, scale: 1, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
                  }}
                  className="text-[#003F72] text-lg md:text-xl font-bold leading-relaxed border-l-4 border-[#0077B6] pl-4"
                >
                  {t(
                    'Από μία αντλία μέχρι ένα ολόκληρο εργοστάσιο.',
                    'From a single pump to an entire factory.'
                  )}
                </motion.p>
              </motion.div>

              <motion.div 
                initial={shouldReduceMotion ? {} : { opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6 }}
                className="relative"
              >
                <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                  <img 
                    src={whoWeAreImage} 
                    alt={t('Η ομάδα μηχανικών της TFT', 'The TFT engineering team')}
                    width={1280}
                    height={960}
                    className="w-full h-auto object-cover"
                    loading="lazy"
                  />
                </div>
              </motion.div>
            </div>

            {/* Stats Section */}
            <div className="mt-12 md:mt-16">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-10 md:gap-8">
                <StatCircle value={63} label={t('χρόνια εμπειρίας', 'years of experience')} delay={0} />
                <StatCircle value={50} suffix="+" label={t('συνεργαζόμενοι προμηθευτές', 'partner suppliers')} delay={0.15} />
                <StatCircle value={500} suffix="+" label={t('πελάτες', 'customers')} delay={0.3} />
                <StatCircle value={100} suffix="+" label={t('projects', 'projects')} delay={0.45} />
                <StatCircle value={35} suffix="+" label={t('έργα με το κλειδί στο χέρι', 'turnkey projects')} delay={0.6} />
              </div>
            </div>

            {/* Inspirational Quote */}
            <motion.div
              initial={shouldReduceMotion ? {} : { opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-16 md:mt-20 max-w-4xl mx-auto text-center"
            >
              <div className="relative px-6 py-8 md:px-12 md:py-10">
                <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 text-7xl md:text-8xl text-[#0077B6]/20 font-serif leading-none select-none">
                  "
                </span>
                <p className="text-xl md:text-2xl lg:text-3xl text-[#003F72] font-medium italic leading-relaxed">
                  {t(
                    'Είμαστε εκεί σε κάθε μεγάλο έργο στη Βιομηχανία Τροφίμων και Ποτών. Κάθε φορά, σε κάθε έργο, με το ίδιο πάθος για καινοτομία και επιτυχία. Μέχρι το επόμενο.',
                    'We are there for every major project in the Food and Beverage Industry. Every time, on every project, with the same passion for innovation and success. Until the next one.'
                  )}
                </p>
                <div className="mt-6 mx-auto h-1 w-20 bg-gradient-to-r from-transparent via-[#0077B6] to-transparent rounded-full" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* Vision Section */}
        <section className="py-12 md:py-16 bg-white relative overflow-hidden">
          <div className="container mx-auto px-4 max-w-5xl relative">
            <motion.div
              initial={shouldReduceMotion ? {} : { opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5 }}
            >
              <Card className="shadow-lg border-none relative overflow-hidden group hover:shadow-xl transition-all duration-500 hover:-translate-y-1">
                <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-[#0077B6] to-[#003F72]" />
                <CardContent className="p-8 md:p-12 relative">
                  <div className="flex flex-col md:flex-row items-start gap-6">
                    <motion.div
                      initial={shouldReduceMotion ? {} : { opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true, amount: 0.2 }}
                      transition={{ duration: 0.5 }}
                      whileHover={{ scale: 1.05 }}
                      className="p-4 bg-gradient-to-br from-[#0077B6]/15 to-[#003F72]/15 rounded-2xl flex-shrink-0 relative"
                    >
                      <Eye className="h-10 w-10 md:h-12 md:w-12 text-[#003F72] relative" />
                    </motion.div>
                    <div>
                      <h2 className="text-3xl md:text-4xl font-bold text-[#003F72] mb-4">
                        {t('Το Όραμά μας', 'Our Vision')}
                      </h2>
                      <p className="text-lg md:text-xl text-[#003F72]/70" style={{ lineHeight: 1.4 }}>
                        {t(
                          'Να ηγούμαστε στις τεχνολογικές εφαρμογές για τη βιομηχανία τροφίμων και ποτών, προσφέροντας στους συνεργάτες μας ανταγωνιστικό πλεονέκτημα και βιώσιμη ανάπτυξη.',
                          'To pioneer in food technology by integrating world-class process, packaging & lab solutions - empowering our partners to achieve sustainable growth.'
                        )}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-12 md:py-16 bg-[#F5F7FB] relative overflow-hidden">
          <div className="container mx-auto px-4 max-w-5xl relative">
            <motion.div
              initial={shouldReduceMotion ? {} : { opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5 }}
            >
              <Card className="shadow-lg border-none relative overflow-hidden group hover:shadow-xl transition-all duration-500 hover:-translate-y-1">
                <div className="absolute right-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-[#003F72] to-[#0077B6]" />
                <CardContent className="p-8 md:p-12 relative">
                  <div className="flex flex-col md:flex-row items-start gap-6">
                    <motion.div
                      initial={shouldReduceMotion ? {} : { opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true, amount: 0.2 }}
                      transition={{ duration: 0.5 }}
                      whileHover={{ scale: 1.05 }}
                      className="p-4 bg-gradient-to-br from-[#0077B6]/15 to-[#003F72]/15 rounded-2xl flex-shrink-0 relative"
                    >
                      <Target className="h-10 w-10 md:h-12 md:w-12 text-[#003F72] relative" />
                    </motion.div>
                    <div>
                      <h2 className="text-3xl md:text-4xl font-bold text-[#003F72] mb-4">
                        {t('Η Αποστολή μας', 'Our Mission')}
                      </h2>
                      <div className="space-y-4 text-lg md:text-xl text-[#003F72]/70" style={{ lineHeight: 1.4 }}>
                        <p>
                          {t(
                            'Να προσφέρουμε αξιόπιστες υπηρεσίες και λύσεις που ενισχύουν τους πελάτες μας, αξιοποιώντας την τεχνολογία, την εμπειρία μας και την προσήλωσή μας στην ποιότητα.',
                            'To provide reliable services and solutions that empower our customers, leveraging technology, our expertise, and our commitment to quality.'
                          )}
                        </p>
                        <p>
                          {t(
                            'Να δημιουργούμε μακροχρόνιες σχέσεις με πελάτες, προμηθευτές και συνεργάτες δίνοντας έμφαση στην αξιοπιστία μας, την καινοτομία και τις βιώσιμες πρακτικές.',
                            'To build long-term relationships with customers, suppliers, and partners, emphasizing reliability, innovation, and sustainable practices.'
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-12 md:py-16 bg-white relative overflow-hidden">
          <div className="container mx-auto px-4 max-w-6xl relative">
            <motion.div
              initial={shouldReduceMotion ? {} : { opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-5xl font-bold text-[#003F72] mb-4">
                {t('Οι Αξίες μας', 'Our Values')}
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {values.map((value, index) => {
                const Icon = value.icon;
                return (
                  <motion.div
                    key={index}
                    initial={shouldReduceMotion ? {} : { opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.4, delay: index * 0.08 }}
                    whileHover={{ y: -4 }}
                  >
                    <Card className="h-full shadow-lg border-none hover:shadow-xl transition-all duration-500 group relative overflow-hidden">
                      {/* Top gradient accent */}
                      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#0077B6] to-[#003F72] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                      {/* Hover gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-br from-[#0077B6]/0 via-[#0077B6]/0 to-[#003F72]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                      <CardContent className="p-8 relative">
                        <motion.div
                          initial={shouldReduceMotion ? {} : { opacity: 0, scale: 0.8 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true, amount: 0.2 }}
                          transition={{ duration: 0.4, delay: index * 0.08 + 0.1 }}
                          whileHover={{ scale: 1.05 }}
                          className={`inline-flex p-4 ${value.bgColor} rounded-2xl mb-6 relative`}
                        >
                          <Icon className={`h-8 w-8 ${value.color}`} />
                        </motion.div>
                        <h3 className="text-xl md:text-2xl font-bold text-[#003F72] mb-4 group-hover:text-[#0077B6] transition-colors duration-300">
                          {t(value.titleEl, value.titleEn)}
                        </h3>
                        <p className="text-[#003F72]/70 leading-relaxed" style={{ lineHeight: 1.4 }}>
                          {t(value.descEl, value.descEn)}
                        </p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Promise Section */}
        <section className="py-12 md:py-16 bg-[#F5F7FB]">
          <motion.div 
            initial={shouldReduceMotion ? {} : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="container mx-auto px-4 max-w-4xl"
          >
            <Card className="shadow-xl border-[#0077B6]/20 border-2 bg-gradient-to-br from-[#0077B6]/5 to-[#003F72]/5 backdrop-blur">
              <CardContent className="p-8 md:p-12 text-center">
                <motion.div 
                  initial={shouldReduceMotion ? {} : { scale: 0.9, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.4 }}
                  className="inline-flex p-4 bg-[#003F72]/10 rounded-full mb-6"
                >
                  <Users2 className="h-12 w-12 text-[#003F72]" />
                </motion.div>
                <h2 className="text-2xl md:text-4xl font-bold text-[#003F72] mb-6">
                  {t('Η Δέσμευσή μας', 'Our Commitment')}
                </h2>
                <p className="text-lg md:text-2xl text-[#003F72]/80 leading-relaxed" style={{ lineHeight: 1.3 }}>
                  {t(
                    'Δεσμευόμαστε να δημιουργούμε αξία μέσα από καινοτομία, συνέπεια και συνεργασία — με διαφάνεια και εμπιστοσύνη σε κάθε συνεργασία, από τον σχεδιασμό έως τη μακροχρόνια υποστήριξη.',
                    'We are committed to creating value through innovation, consistency and teamwork — with transparency and trust in every partnership, from design to long-term support.'
                  )}
                </p>
                <motion.div
                  initial={shouldReduceMotion ? {} : { opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                  className="mt-8"
                >
                  <Button
                    onClick={() => navigate('/presentation')}
                    size="lg"
                    className="bg-[#003F72] hover:bg-[#0077B6] text-white gap-2"
                  >
                    <PresentationIcon className="h-5 w-5" />
                    {t('Προβολή Παρουσίασης', 'View Presentation')}
                  </Button>
                </motion.div>
              </CardContent>
            </Card>
          </motion.div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default Company;